import { EquipmentRecordStatus, ProgramType, ProgramTypeLabels } from 'types';

export const emptyValueLabel = '--';

/**
 * A manufacturer's brands render as one list with no pagination of their own, and the largest real
 * manufacturer has ten, so a single page always covers them. Capped at the API's own page maximum.
 */
export const brandPageLimit = 200;

export const EquipmentRecordStatusLabels: Record<EquipmentRecordStatus, string> = {
  [EquipmentRecordStatus.active]: 'Active',
  [EquipmentRecordStatus.inactive]: 'Inactive',
};

/** The DSM tag palette has no neutral-positive hue: active=green, inactive=grey (a dormant state). */
export const EquipmentRecordStatusTagColors: Record<EquipmentRecordStatus, 'success' | 'grey'> = {
  [EquipmentRecordStatus.active]: 'success',
  [EquipmentRecordStatus.inactive]: 'grey',
};

export const programTypeOptions = Object.values(ProgramType).map((value) => ({
  value,
  label: ProgramTypeLabels[value],
}));

export const statusOptions = Object.values(EquipmentRecordStatus).map((value) => ({
  value,
  label: EquipmentRecordStatusLabels[value],
}));

export const programTypeValues: readonly string[] = Object.values(ProgramType);
export const statusValues: readonly string[] = Object.values(EquipmentRecordStatus);

/** Query params are user-editable, so a raw cast would hand a bogus enum value to the API. */
export const enumParam = <T extends string>(raw: string | null, allowed: readonly string[]): T | '' =>
  raw && allowed.includes(raw) ? (raw as T) : '';

export const pageParam = (raw: string | null): number => {
  const parsed = Math.floor(Number(raw));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const manufacturerFieldLabels: Record<string, string> = {
  name: 'Name',
  programTypes: 'Program Types',
  aliases: 'Aliases',
  status: 'Status',
};

const brandFieldLabels: Record<string, string> = {
  name: 'Name',
  manufacturerId: 'Manufacturer',
  websiteUrl: 'Website',
  warrantyRegistrationUrl: 'Warranty Registration',
  aliases: 'Aliases',
  status: 'Status',
};

export const manufacturerFieldLabel = (field: string): string => manufacturerFieldLabels[field] ?? field;
export const brandFieldLabel = (field: string): string => brandFieldLabels[field] ?? field;

/**
 * Format a change-history value for display. Handles the three non-scalar shapes these entities
 * store — aliases, program type arrays, and status codes — and falls back to the raw string.
 */
export const formatEquipmentHistoryValue = (field: string, value: unknown): string => {
  if (value === undefined || value === null || value === '') return emptyValueLabel;

  if (field === 'status' && typeof value === 'string') {
    return EquipmentRecordStatusLabels[value as EquipmentRecordStatus] ?? value;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return emptyValueLabel;
    if (value.every((entry) => entry && typeof entry === 'object' && 'value' in entry)) {
      const aliases = value as { source?: string; value: string }[];
      return aliases.map((alias) => (alias.source ? `${alias.source}: ${alias.value}` : alias.value)).join(', ');
    }
    if (field === 'programTypes') {
      return (value as ProgramType[]).map((programType) => ProgramTypeLabels[programType] ?? programType).join(', ');
    }
    return value.join(', ');
  }

  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
};
